import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, getFormAsyncErrors, getFormSyncErrors, getFormValues, Field } from 'redux-form';

import { ReduxFormInput as Input } from '@ott/input-next-deprecated';
import FirstErrorShower from '@ott/first-error-shower';
import AuthErrorShower from '@ott/auth-error-shower';
import Button from '@ott/button';

import rules from '@ott/utility-form-validate-rules';
import validate from '@ott/utility-form-validate';
import { l10n, l10nhtml } from '@ott/l10n';

import { SIGN_UP_FORM_NAME } from '../../constants';
import { register, regReset } from '../../redux/auth/actions';

const minLen6 = (v) => v.length >= 6;
const equalPasswords = (password2, { password }, props) => password === password2;

const validateForm = validate({
  email: [
    { msg: l10n('auth.emailRequired'), validate: rules.isRequired },
    { msg: l10n('auth.emailFormat'), validate: rules.isEmail },
  ],
  password: [{ msg: l10n('auth.passwordRequired'), validate: rules.isRequired }],
  password2: [{ msg: l10n('auth.passwordRepeat'), validate: rules.isRequired }],
});

const asyncValidate = validate({
  password2: [{ msg: l10n('auth.passwordsNotEqual'), validate: equalPasswords }],
  password: [{ msg: l10n('auth.passwordShort'), validate: minLen6 }],
});

import styles from './SignupForm.scss';

const componentName = 'SignupForm';
export const FORM_NAME = SIGN_UP_FORM_NAME;

@reduxForm({
  form: FORM_NAME,
  validate: validateForm,
  asyncBlurFields: ['password2', 'password'],
  asyncValidate: (values, props) => {
    return new Promise((resolve, reject) => {
      const errors = asyncValidate(values, props);
      Object.keys(errors).length ? reject(errors) : resolve(errors);
    });
  },
  onSubmit: ({ phone, email, password, password2 }, dispatch, { reqHost }) => {
    dispatch(register({ phone, email, password, password2, reqHost }));
  },
})
@connect((state) => {
  const {
    common: { auth },
  } = state;
  const asyncFormErrors = getFormAsyncErrors(FORM_NAME)(state);

  return {
    status: auth.registerStatus,
    hasAsyncErrors: asyncFormErrors && Object.keys(asyncFormErrors).length > 0,
    syncFormErrors: getFormSyncErrors(FORM_NAME)(state),
    formValues: getFormValues(FORM_NAME)(state),
    asyncFormErrors,
  };
})
class SignupForm extends AuthErrorShower {
  static propTypes = {
    status: PropTypes.object,
    requestSignin: PropTypes.func,
    onClose: PropTypes.func,
  };

  state = {};

  static defaultProps = {};

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    const { dispatch } = this.props;

    dispatch(regReset());

    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    switch (e.keyCode) {
      case 13:
        e.preventDefault();
        this.props.handleSubmit();
      default:
        break;
    }
  };

  handleSubmit = (e) => {
    this.setState({
      submitTime: new Date().getTime(),
    });
    e.preventDefault();

    this.props.handleSubmit();
  };

  renderAuthLink(requestSignin) {
    if (!requestSignin) {
      return null;
    }

    return (
      <div className={styles.signupFormFooter}>
        {l10n('auth.alreadyHave')}{' '}
        <a
          data-locator={`${componentName}--goToAuth`}
          className={styles.link}
          onClick={requestSignin}
        >
          {l10n('auth.signIn')}
        </a>
      </div>
    );
  }

  // override
  renderError(error) {
    if (!error) {
      return null;
    }

    return (
      <div data-locator={`${componentName}--error`} className={styles.error}>
        {super.renderError(error)}
      </div>
    );
  }

  render() {
    const {
      formValues,
      onClose,
      status,
      requestSignin,
      syncFormErrors,
      asyncFormErrors,
      submitFailed,
      hasAsyncErrors,
    } = this.props;

    if (status.success && formValues && formValues.email) {
      return (
        <div className={styles.SignupForm}>
          <div className={styles.confirmEmail}>
            {l10n('auth.confirmEmail', { email: formValues.email })}
          </div>
          <div className={styles.letterSpam}>{l10n('auth.letterSpam')}</div>
          <Button
            className={styles.button}
            onClick={onClose}
            specificLocators={[`${componentName}--close`]}
            withFullWidth={true}
          >
            {l10n('auth.close')}
          </Button>
        </div>
      );
    }

    const errorNode = this.renderError(status.errorData);
    const authLinkNode = this.renderAuthLink(requestSignin);

    const isLoading = status.loading;

    return (
      <div className={styles.SignupForm}>
        <form>
          <FirstErrorShower
            errors={{
              ...(submitFailed ? syncFormErrors : {}),
              ...asyncFormErrors,
            }}
            show={hasAsyncErrors || submitFailed}
            container={this}
            overlayContainer={this}
            time={this.state.submitTime}
          />
          <div className={styles.text}>{l10n('auth.yourEmail')}</div>
          {errorNode}
          <div className={styles.inputWrapper}>
            <Field
              name="email"
              component={Input}
              type="email"
              disabled={isLoading}
              props={{
                placeholder: l10n('auth.email'),
                autocompete: 'on',
                autoFocus: true,
                'data-locator': `${componentName}--email`,
              }}
            />
          </div>
          <div className={styles.inputWrapper}>
            <Field
              name="password"
              component={Input}
              type="password"
              disabled={isLoading}
              props={{
                placeholder: l10n('auth.password'),
                'data-locator': `${componentName}--pwd`,
              }}
            />
          </div>
          <div className={styles.inputWrapper}>
            <Field
              name="password2"
              component={Input}
              type="password"
              disabled={isLoading}
              props={{
                placeholder: l10n('auth.password2'),
                'data-locator': `${componentName}--pwd2`,
              }}
            />
          </div>
          <div className={styles.agreement}>{l10nhtml('auth.personalDataAgreementRegister')}</div>
          <Button
            className={styles.button}
            isDisabled={status.loading}
            onClick={this.handleSubmit}
            specificLocators={[`${componentName}--submit`]}
            withFullWidth={true}
            isLoading={isLoading}
          >
            {l10n('auth.register')}
          </Button>
        </form>
        {authLinkNode}
      </div>
    );
  }
}

export default SignupForm;
